import { useEffect, useState } from "react";
import { Lock, Users } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { useValidateInviteToken, useAcceptInvite } from "../../../hooks/useAPI";
import {
  AuthShell,
  AuthBrandPanel,
  AuthBanner,
  AuthInput,
  AuthButton,
  AuthInfoCard,
  PasswordRuleChecklist,
  TokenStatePage,
  PWD_RULES,
} from "./_primitives";

// Brand panel factory — i18n-aware. Receives the translation function
// from the parent so the brand panel matches whichever locale is active
// at render time.
function buildInviteBrand(
  t: (key: string, opts?: Record<string, unknown>) => string,
  orgName: string,
  role: string,
  inviterEmail: string,
) {
  const trustLabels = t("acceptInvite.brand.trust", { returnObjects: true }) as unknown as string[];
  return (
    <AuthBrandPanel
      headline={t("acceptInvite.brand.headline", { orgName })}
      intro={
        <Trans
          i18nKey="acceptInvite.brand.intro"
          values={{ role }}
          components={[<strong key="role" style={{ color: "var(--foreground)" }} />]}
        />
      }
      body={
        <div className="space-y-3">
          <h3 className="editorial-eyebrow">{t("acceptInvite.brand.detailsTitle")}</h3>
          <dl className="space-y-2 text-sm" style={{ lineHeight: 1.55 }}>
            <div className="flex items-baseline gap-3">
              <dt className="auth-mono-label flex-shrink-0" style={{ minWidth: 80 }}>{t("acceptInvite.brand.workspace")}</dt>
              <dd className="text-[var(--foreground)] break-words min-w-0 flex-1">{orgName}</dd>
            </div>
            <div className="flex items-baseline gap-3">
              <dt className="auth-mono-label flex-shrink-0" style={{ minWidth: 80 }}>{t("acceptInvite.brand.yourRole")}</dt>
              <dd className="text-[var(--foreground)] min-w-0 flex-1">{role}</dd>
            </div>
            <div className="flex items-baseline gap-3">
              <dt className="auth-mono-label flex-shrink-0" style={{ minWidth: 80 }}>{t("acceptInvite.brand.invitedBy")}</dt>
              <dd className="break-all min-w-0 flex-1" style={{ fontFamily: "var(--font-mono)", color: "var(--foreground)" }}>{inviterEmail}</dd>
            </div>
          </dl>
        </div>
      }
      trustLabels={Array.isArray(trustLabels) ? trustLabels : []}
    />
  );
}

function buildMinimalBrand(t: (key: string, opts?: Record<string, unknown>) => string) {
  const trustLabels = t("acceptInvite.brand.minimalTrust", { returnObjects: true }) as unknown as string[];
  return (
    <AuthBrandPanel
      headline={t("acceptInvite.brand.minimalHeadline")}
      intro={t("acceptInvite.brand.minimalIntro")}
      trustLabels={Array.isArray(trustLabels) ? trustLabels : []}
    />
  );
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const acceptInvite = useAcceptInvite();
  const { data: invite, isLoading, error: validateError } = useValidateInviteToken(token);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && invite && !done) {
      const id = window.setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('input[name="invite-name"]');
        el?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [isLoading, invite, done]);

  const canSubmit = password.length > 0 && !acceptInvite.isPending;

  const minimalBrand = buildMinimalBrand(t);

  if (!token) {
    return (
      <AuthShell pageTitle={t("acceptInvite.noTokenPageTitle")} pageDescription={t("acceptInvite.noTokenPageDescription")} brand={minimalBrand}>
        <TokenStatePage
          status="no-token"
          noTokenTitle={t("acceptInvite.noTokenTitle")}
          noTokenBody={t("acceptInvite.noTokenBody")}
          noTokenAction={
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {t("common.actions.backToSignIn")}
            </button>
          }
        />
      </AuthShell>
    );
  }

  if (isLoading) {
    return (
      <AuthShell pageTitle={t("acceptInvite.pageTitle")} pageDescription={t("acceptInvite.pageDescription")} brand={minimalBrand}>
        <TokenStatePage status="loading" loadingTitle={t("acceptInvite.validatingTitle")} loadingBody={t("acceptInvite.validatingBody")} />
      </AuthShell>
    );
  }

  if (validateError || !invite) {
    return (
      <AuthShell pageTitle={t("acceptInvite.invalidPageTitle")} pageDescription={t("acceptInvite.invalidPageDescription")} brand={minimalBrand}>
        <TokenStatePage
          status="expired"
          expiredTitle={t("acceptInvite.invalidTitle")}
          expiredBody={t("acceptInvite.invalidBody")}
          expiredAction={
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {t("common.actions.backToSignIn")}
            </button>
          }
        />
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell pageTitle={t("acceptInvite.successPageTitle")} pageDescription={t("acceptInvite.successPageDescription")} brand={minimalBrand}>
        <TokenStatePage
          status="success"
          successTitle={t("acceptInvite.successTitle")}
          successBody={
            <Trans
              i18nKey="acceptInvite.successBody"
              values={{ orgName: invite.organizationName, role: invite.role }}
              components={[
                <strong key="org" className="text-[var(--foreground)] break-words" />,
                <strong key="role" className="text-[var(--foreground)]" />,
              ]}
            />
          }
          successAction={
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {t("acceptInvite.successAction")}
            </button>
          }
        />
      </AuthShell>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (acceptInvite.isPending) return;
    setFormError("");
    if (!password) { setFormError(t("acceptInvite.errors.passwordRequired")); return; }

    acceptInvite.mutate(
      { token, data: { name: name.trim() || undefined, password } },
      {
        onSuccess: () => setDone(true),
        onError: (err) => {
          const message = err instanceof Error ? err.message : "";
          const code = (err as { code?: string } | undefined)?.code || "";
          const lowered = (code + " " + message).toLowerCase();
          if (
            code === "INVALID_TOKEN" || code === "EXPIRED_TOKEN" ||
            lowered.includes("expired") || lowered.includes("invalid token")
          ) {
            setFormError(t("acceptInvite.errors.expired"));
          } else if (code === "EMAIL_MISMATCH" || lowered.includes("email")) {
            setFormError(t("acceptInvite.errors.emailMismatch"));
          } else if (lowered.includes("password")) {
            setFormError(t("acceptInvite.errors.passwordWrong"));
          } else {
            setFormError(t("acceptInvite.errors.generic"));
          }
        },
      },
    );
  };

  return (
    <AuthShell
      pageTitle={t("acceptInvite.pageTitle")}
      pageDescription={t("acceptInvite.pageDescription")}
      brand={buildInviteBrand(t, invite.organizationName, invite.role, invite.email)}
    >
      <div className="space-y-6">
        <header>
          <h1 className="editorial-display">
            {t("acceptInvite.title", { orgName: invite.organizationName })}
          </h1>
          <p className="editorial-subtitle">{t("acceptInvite.subtitle")}</p>
        </header>

        <AuthInfoCard
          icon={Users}
          title={invite.organizationName}
          monoMeta={`INVITED AS · ${invite.role.toUpperCase()} · ${invite.email}`}
        >
          <Trans
            i18nKey="acceptInvite.invitedCardBody"
            values={{ role: invite.role }}
            components={[<strong key="role" className="text-[var(--foreground)]" />]}
          />
        </AuthInfoCard>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {formError && <AuthBanner variant="error">{formError}</AuthBanner>}

          <AuthInput
            label={t("acceptInvite.nameLabel")}
            helper={t("acceptInvite.nameHelper")}
            type="text"
            name="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("acceptInvite.namePlaceholder")}
            autoComplete="name"
            autoCapitalize="words"
            disabled={acceptInvite.isPending}
          />

          <div>
            <AuthInput
              label={t("acceptInvite.passwordLabel")}
              type="password"
              name="invite-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("acceptInvite.passwordPlaceholder")}
              autoComplete="current-password"
              autoCapitalize="off"
              spellCheck={false}
              disabled={acceptInvite.isPending}
              leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
              withPasswordToggle
              helper={t("acceptInvite.passwordHelper")}
              aria-describedby="acceptinvite-pwd-rules"
            />
            <PasswordRuleChecklist id="acceptinvite-pwd-rules" password={password} rules={PWD_RULES} />
          </div>

          <AuthButton
            type="submit"
            variant="primary"
            fullWidth
            loading={acceptInvite.isPending}
            loadingLabel={t("acceptInvite.submitLoading")}
            disabled={!canSubmit}
          >
            {t("acceptInvite.submit")}
          </AuthButton>

          <div className="text-center pt-2">
            <Link to="/login" className="auth-tertiary">
              {t("acceptInvite.signInAsDifferent")}
            </Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}
